import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  TableCellsIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import EmptyModal from "../../components/modals/EmptyModal";
import {
  ProductPricingImportApiError,
  downloadProductPricingImportFile,
  downloadProductPricingTemplate,
  listProductPricingImports,
  retryProductPricingImport,
  uploadProductPricingImport,
} from "../../shared/productPricingImportApi";
import type {
  ProductPricingImport,
  ProductPricingImportType,
} from "../../types/productPricingImports";

const activeStatuses = new Set(["validating", "queued", "processing"]);

const importActions: Array<{
  type: ProductPricingImportType;
  title: string;
  description: string;
  downloadLabel: string;
  uploadLabel: string;
}> = [
  {
    type: "cost",
    title: "Product costs",
    description:
      "Change only Cost Price. Item names and codes are for reference and are never changed.",
    downloadLabel: "Download cost template",
    uploadLabel: "Upload product costs",
  },
  {
    type: "price",
    title: "Product prices",
    description:
      "Change only Unit Price. Item names and codes are for reference and are never changed.",
    downloadLabel: "Download price template",
    uploadLabel: "Upload product prices",
  },
  {
    type: "group-price",
    title: "Group prices",
    description:
      "Set Available to Yes or No. Enter a Price for a group-specific price, or leave Price blank to keep the product available at its unit price.",
    downloadLabel: "Download group price template",
    uploadLabel: "Upload group prices",
  },
];

const statusStyle = (status: ProductPricingImport["status"]) => {
  if (status === "completed") return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (status === "rejected" || status === "failed") return "bg-red-50 text-red-700 ring-red-200";
  return "bg-amber-50 text-amber-700 ring-amber-200";
};

const workbookAccept = {
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
};

function WorkbookUploadDropzone({
  title,
  error,
  onClose,
  onFile,
  onReject,
}: {
  title: string;
  error: string | null;
  onClose: () => void;
  onFile: (file: File) => void;
  onReject: () => void;
}) {
  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <p className="mb-4 mt-2 text-gray-500">{title}</p>
        <button
          type="button"
          className="rounded-md bg-white text-gray-900 hover:text-gray-500"
          onClick={onClose}
        >
          <span className="sr-only">Close</span>
          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
      <Dropzone
        accept={workbookAccept}
        maxFiles={1}
        multiple={false}
        onDrop={(files) => {
          const file = files[0];
          if (file) onFile(file);
        }}
        onDropRejected={onReject}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div
            {...getRootProps({
              className: `relative block w-full rounded-lg border-2 border-dashed p-8 text-center cursor-pointer ${
                isDragActive ? "border-indigo-500 bg-indigo-50" : "border-blue-300 bg-blue-50"
              }`,
            })}
          >
            <input {...getInputProps()} />
            <p className="text-gray-700">
              {isDragActive
                ? "Drop the workbook here"
                : "Drag an Excel workbook here, or click to choose a file"}
            </p>
            <p className="mt-2 text-sm text-gray-500">.xlsx files only</p>
          </div>
        )}
      </Dropzone>
      {error && <p className="mt-4 text-center text-sm text-red-600">{error}</p>}
    </div>
  );
}

const formatDate = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
        new Date(value)
      )
    : "—";

export default function ProductPricingImports() {
  const [uploadType, setUploadType] = useState<ProductPricingImportType | null>(null);
  const [dropError, setDropError] = useState<string | null>(null);
  const [imports, setImports] = useState<ProductPricingImport[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [checkingType, setCheckingType] = useState<ProductPricingImportType | null>(null);
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    ProductPricingImport["validation_errors"]
  >([]);

  const refresh = useCallback(async () => {
    try {
      const result = await listProductPricingImports();
      setImports(result.data);
    } catch {
      setMessage({ tone: "error", text: "Import history could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!imports.some((item) => activeStatuses.has(item.status))) return;
    const timer = window.setInterval(() => void refresh(), 5000);
    return () => window.clearInterval(timer);
  }, [imports, refresh]);

  const upload = async (type: ProductPricingImportType, file?: File) => {
    if (!file) return;
    setBusy(true);
    setCheckingType(type);
    setMessage(null);
    setValidationErrors([]);
    try {
      const result = await uploadProductPricingImport(type, file);
      setMessage({ tone: "success", text: result.message });
      await refresh();
    } catch (error: unknown) {
      const apiError = error as ProductPricingImportApiError;
      setMessage({ tone: "error", text: apiError.message });
      setValidationErrors(apiError.response?.import.validation_errors || []);
      await refresh();
    } finally {
      setBusy(false);
      setCheckingType(null);
    }
  };

  const closeUploadModal = () => {
    setUploadType(null);
    setDropError(null);
  };

  const retry = async (id: string) => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await retryProductPricingImport(id);
      setMessage({ tone: "success", text: result.message });
      await refresh();
    } catch (error: unknown) {
      setMessage({ tone: "error", text: (error as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-indigo-50 p-3 text-indigo-600">
                <TableCellsIcon className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Product imports</h1>
                <p className="mt-1 text-sm text-slate-500">
                  Bulk update product costs, prices, and group prices, then track every import.
                </p>
              </div>
            </div>
            <p className="mt-5 max-w-3xl text-sm leading-6 text-slate-600">
              Download a template, edit the highlighted columns, then upload it here. Every
              FluxOrders ID and customer group is checked against your company before any job is
              queued.
            </p>
          </div>
        </div>
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          {importActions.map((action) => {
            const typeIsActive = imports.some(
              (item) => item.type === action.type && activeStatuses.has(item.status)
            );
            return (
              <div key={action.type} className="rounded-xl border border-slate-200 p-4">
                <h2 className="text-sm font-semibold text-slate-900">{action.title}</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">{action.description}</p>
                <div className="mt-4 flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => void downloadProductPricingTemplate(action.type)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" /> {action.downloadLabel}
                  </button>
                  <button
                    type="button"
                    disabled={busy || typeIsActive}
                    onClick={() => {
                      setDropError(null);
                      setUploadType(action.type);
                    }}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ArrowPathIcon
                      className={`h-5 w-5 ${checkingType === action.type ? "animate-spin" : ""}`}
                    />
                    {checkingType === action.type ? "Checking workbook…" : action.uploadLabel}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {message && (
          <div
            role="alert"
            className={`mt-6 rounded-xl px-4 py-3 text-sm ${
              message.tone === "success"
                ? "bg-emerald-50 text-emerald-800"
                : "bg-red-50 text-red-800"
            }`}
          >
            {message.text}
          </div>
        )}
        {validationErrors.length > 0 && (
          <ul className="mt-3 space-y-1 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-800">
            {validationErrors.map((error, index) => (
              <li key={`${error.row}-${error.field}-${index}`}>
                {error.row ? `Row ${error.row}, ` : ""}
                {error.field}: {error.message}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-6 py-5">
          <h2 className="text-lg font-semibold text-slate-900">Import history</h2>
          <p className="mt-1 text-sm text-slate-500">
            Uploaded workbooks remain private and can be downloaded by administrators.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
              <tr>
                <th className="px-6 py-3">File</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Products</th>
                <th className="px-6 py-3">Requested</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : imports.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-slate-500">
                    No imports yet.
                  </td>
                </tr>
              ) : (
                imports.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4">
                      <p className="font-medium text-slate-900">{item.filename}</p>
                      <p className="mt-1 text-xs text-slate-500">{item.type_label}</p>
                      {item.error_message && (
                        <p className="mt-1 text-xs text-red-600">{item.error_message}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold capitalize ring-1 ring-inset ${statusStyle(item.status)}`}
                      >
                        {activeStatuses.has(item.status) ? (
                          <ClockIcon className="h-4 w-4" />
                        ) : item.status === "completed" ? (
                          <CheckCircleIcon className="h-4 w-4" />
                        ) : (
                          <ExclamationTriangleIcon className="h-4 w-4" />
                        )}
                        {item.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      {item.updated_rows} / {item.total_rows}
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      <p>{formatDate(item.requested_at)}</p>
                      {item.requested_by && (
                        <p className="mt-1 text-xs text-slate-400">{item.requested_by.name}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            void downloadProductPricingImportFile(item.id, item.filename)
                          }
                          className="text-sm font-semibold text-indigo-600 hover:text-indigo-800"
                        >
                          Download
                        </button>
                        {item.can_retry && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void retry(item.id)}
                            className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
      <EmptyModal
        childComponent={
          uploadType ? (
            <WorkbookUploadDropzone
              title={`Upload the ${importActions.find((action) => action.type === uploadType)?.title.toLowerCase()} workbook`}
              error={dropError}
              onClose={closeUploadModal}
              onReject={() => setDropError("Choose an Excel .xlsx workbook.")}
              onFile={(file) => {
                const type = uploadType;
                closeUploadModal();
                void upload(type, file);
              }}
            />
          ) : null
        }
        open={uploadType !== null}
        setOpen={closeUploadModal}
      />
    </div>
  );
}
